import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ButtonItem from '../components/ButtonItem';
import AlbumsTable from '../components/AlbumsTable';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import SectionTabs from '../components/SectionTabs';
import { Building2, Mic2, Disc3, Music, Package } from 'lucide-react';

function AlbumPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPage = parseInt(params.get('page')) || 1;
  const initialSearch = params.get('search') || '';

  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchKeyword, setSearchKeyword] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const fetchAlbums = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/album`,
          {
            params: {
              page,
              limit: 10,
              search: searchKeyword,
            },
          }
        );
        setAlbums(response.data.items);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    },
    [searchKeyword]
  );

  useEffect(() => {
    fetchAlbums(currentPage);
  }, [currentPage, fetchAlbums]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const searchParam = searchKeyword ? `&search=${encodeURIComponent(searchKeyword)}` : '';
    navigate(`/Albums?page=${newPage}${searchParam}`);
  };

  const handleDownload = async () => {
    try {
      const params = { search: searchTerm, page: 1, limit: totalCount };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/album`,
        { params }
      );
      const allAlbums = response.data.items;

      const exportAlbums = allAlbums.filter((a) =>
        a.nameKr.includes(searchTerm)
      );

      const exportData = exportAlbums.map((a, idx) => {
        return {
          앨범코드: a.code,
          앨범명: a.nameKr,
          권리사: a.clientDisplay || '',
          아티스트명: a.artist.nameKr || '',
          발매일: a.releaseDateTime || '',
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '앨범');
      XLSX.writeFile(wb, `앨범.xlsx`);
    } catch (error) {
      console.error('엑셀 다운로드 중 에러:', error);
    }
  };

  const dataTabs = [
    { label: '권리사', to: '/Clients', icon: Building2 },
    { label: '아티스트', to: '/Artists', icon: Mic2 },
    { label: '앨범', to: '/Albums', icon: Disc3 },
    { label: '곡', to: '/Songs', icon: Music },
    { label: '음반', to: '/PhysicalAlbums', icon: Package },
  ];

  return (
    <>
        <div className="flex mb-8 justify-center items-center">
          <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            <span>앨범 리스트</span>
          </div>
        </div>
        <SectionTabs items={dataTabs} />

        <SearchInput
          placeholder="앨범명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1);
            setSearchKeyword(searchTerm);
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            navigate(`/Albums?page=1${searchParam}`);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-400">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <AlbumsTable
              data={albums}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={10}
              searchKeyword={searchKeyword}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            <div className="w-full flex justify-end items-center mt-6">
              <ButtonItem
                text="등록"
                color="blue"
                onClick={() => navigate('/AlbumWrite')}
              />
              <button
                onClick={handleDownload}
                className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors ml-2">
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                다운로드
              </button>
            </div>
          </>
        )}
    </>
  );
}

export default AlbumPage;
